<template>
  <v-layout column>
    <v-layout align-center class="mb-3">
      <div class="title">UltraTax Input Screens Prune Sets</div>
      <v-spacer />
      <div style="width: 120px" class="mr-2">
        <v-text-field
          v-model="year"
          class="default-input"
          hide-details
          solo
          label="Year"
          type="number"
        />
      </div>
    </v-layout>

    <v-layout align-center class="mb-4" wrap>
      <div class="caption" style="width: 100%;">
        For each document in the list below, open it, match each prune set with
        an entity, then click "Execute" to remove those items from the
        entity.<br />
        This tool is only visible to Soraban employees.
      </div>
    </v-layout>

    <v-layout align-center class="mb-4" wrap>
      <div class="subheading font-weight-bold">
        Textract Processing Progress
      </div>
      <v-btn
        small
        class="primary text-none"
        :loading="priorYearDocStatusLoading"
        @click="loadPriorYearDocStatus"
      >
        Refresh
      </v-btn>
      <v-progress-linear
        class="mr-3"
        :value="importProgressPercent"
        height="10"
        style="max-width: 420px"
        :color="isImportReady ? 'success' : 'primary'"
      />
      <div class="caption mr-3" v-if="priorYearDocStatusLoading">
        Loading…
      </div>
      <div class="caption mr-3" v-else-if="importTotalCount === 0">
        No UltraTax input screen documents found for {{ year }}.
      </div>
      <div class="caption mr-3" v-else>
        <span v-if="isImportReady">
          Ready for QA (all UltraTax input screens for {{ year }} imported)
        </span>
        <span v-else>
          {{ documentsInProgress.length }} processing,
          {{ successfullyImportedDocuments.length }}
          completed
        </span>
      </div>
    </v-layout>

    <v-layout align-center class="mb-2">
      <div class="subheading font-weight-bold">
        Documents with prune sets ({{ documents.length }} remaining)
      </div>
      <v-spacer />
      <v-btn
        small
        class="primary text-none"
        :loading="documentsLoading"
        @click="loadDocuments"
      >
        Refresh documents
      </v-btn>
      <v-btn
        small
        class="error text-none ml-2"
        :loading="deletingAllPruneSets"
        :disabled="documentsLoading || deletingAllPruneSets"
        @click="confirmDeleteAllPruneSets"
      >
        Clear all documents with prune sets
      </v-btn>
      <v-btn
        small
        class="default-input error text-none"
        hide-details
        :loading="removingUploadedFiles"
        @click="removeAllUploadedFilesForRolloverItems"
      >
        Remove all uploaded files
      </v-btn>
    </v-layout>

    <div class="documents-table-wrapper mb-6">
      <v-data-table
        class="documents-data-table"
        :headers="documentsHeaders"
        :items="documents"
        :loading="documentsLoading"
        disable-initial-sort
        :rows-per-page-items="[10, 25, 50]"
      >
        <template v-slot:items="props">
          <tr>
            <td class="text-xs-left">
              <div class="font-weight-bold">{{ props.item.name }}</div>
              <div class="caption">
                #{{ props.item.id }} • {{ props.item.status }} •
                {{ props.item.created_at }}
              </div>
            </td>
            <td class="text-xs-left">{{ props.item.prune_set_count }}</td>
            <td class="text-xs-left">{{ props.item.remaining_count }}</td>
            <td class="text-xs-left">{{ props.item.unmatched_count }}</td>
            <td class="text-xs-left">{{ props.item.ready_count }}</td>
            <td class="text-xs-left">{{ props.item.pruned_count }}</td>
            <td class="text-xs-left">
              <v-btn
                small
                class="primary text-none"
                @click="openDocument(props.item.id)"
              >
                Open
              </v-btn>
            </td>
          </tr>
        </template>
      </v-data-table>
    </div>
    <div style="height: 24px;"></div>
    <v-layout align-center class="mb-2">
      <div class="subheading font-weight-bold">Prune Sets</div>
      <v-spacer />
      <v-btn
        small
        class="primary text-none"
        :loading="loading"
        :disabled="!canLoad"
        @click="loadPruneSets"
      >
        Refresh loaded prune sets for opened document
      </v-btn>
    </v-layout>
    <v-data-table
      :headers="headers"
      :items="pruneSets"
      :loading="loading"
      disable-initial-sort
      :rows-per-page-items="[25, 50, 100]"
    >
      <template v-slot:items="props">
        <tr>
          <td class="text-xs-left">
            <strong>Prune Set ID:</strong> {{ props.item.id }}<br />
            <strong>Status:</strong> {{ props.item.status }}
          </td>
          <td class="text-xs-left">
            <strong>Taxpayer:</strong> {{ props.item.fname }}
            {{ props.item.lname }}<br />
            <strong>Email:</strong> {{ props.item.email }}<br />
            <div v-if="props.item.spouse_fname || props.item.spouse_lname">
              <strong>Spouse:</strong> {{ props.item.spouse_fname }}
              {{ props.item.spouse_lname }}
            </div>
            <div v-if="props.item.spouse_email">
              <strong>Spouse Email:</strong> {{ props.item.spouse_email }}
            </div>
            <div v-if="props.item.client_id">
              <strong>Client ID:</strong> {{ props.item.client_id }}
            </div>
          </td>
          <td class="text-xs-left">
            {{ props.item.items_count }}
          </td>
          <td class="text-xs-left">
            <v-autocomplete
              class="default-input body-1"
              hide-details
              :loading="loadingCompanies"
              :disabled="loadingCompanies"
              solo
              clearable
              :items="companiesWithEmail"
              :value="props.item.company_id"
              @change="newCompanyId => saveCompany(props.item, newCompanyId)"
              @click:clear="saveCompany(props.item, null)"
              placeholder="Select entity"
              item-value="id"
              item-text="nameWithEmail"
            />
          </td>
          <td class="text-xs-left pa-0">
            <v-layout column>
              <v-btn
                small
                class="primary text-none"
                style="min-width: 120px;"
                :disabled="!props.item.company_id"
                :loading="running[props.item.id] === 'dry_run'"
                @click="dryRun(props.item)"
              >
                Dry-run
              </v-btn>
              <v-btn
                small
                class="error text-none"
                style="min-width: 120px;"
                :disabled="!props.item.company_id"
                :loading="running[props.item.id] === 'execute'"
                @click="executePrune(props.item)"
              >
                Remove items (prune)
              </v-btn>
              <v-btn
                small
                text
                color="error text-none"
                class="mt-1"
                style="min-width: 120px;"
                @click="deletePruneSet(props.item)"
              >
                Delete
              </v-btn>
            </v-layout>
          </td>
          <td class="text-xs-left">
            <div
              v-if="
                props.item.preview &&
                  props.item.preview.would_delete_count != null
              "
            >
              Total current PYD items: {{ props.item.preview.candidate_count }}
              <div
                class="caption"
                v-if="
                  props.item.preview.sample_would_delete_names &&
                    props.item.preview.sample_would_delete_names.length
                "
              >
                <div class="font-weight-bold">
                  Delete list: ({{ props.item.preview.would_delete_count }} to
                  prune;
                  {{
                    props.item.preview.candidate_count -
                      props.item.preview.would_delete_count
                  }}
                  after prune)
                </div>
                <div
                  v-for="(name, idx) in props.item.preview
                    .sample_would_delete_names"
                  :key="idx"
                >
                  {{ name }}
                </div>
              </div>
            </div>
          </td>
        </tr>
      </template>
    </v-data-table>
  </v-layout>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import API from '@/api';

export default {
  data() {
    return {
      year: new Date().getFullYear() - 1,
      extractedDocumentId: '',
      priorYearDocStatusLoading: false,
      successfullyImportedDocuments: [],
      documentsInProgress: [],
      documents: [],
      documentsLoading: false,
      removingUploadedFiles: false,
      deletingAllPruneSets: false,
      documentsHeaders: [
        { text: 'Document', value: 'name', sortable: false, width: '50%' },
        {
          text: 'Total sets',
          value: 'prune_set_count',
          sortable: false,
          width: '8%',
        },
        {
          text: 'Remaining',
          value: 'remaining_count',
          sortable: false,
          width: '8%',
        },
        {
          text: 'Unmatched',
          value: 'unmatched_count',
          sortable: false,
          width: '8%',
        },
        { text: 'Ready', value: 'ready_count', sortable: false, width: '8%' },
        { text: 'Pruned', value: 'pruned_count', sortable: false, width: '8%' },
        { text: '', value: 'open', sortable: false, width: '10%' },
      ],
      pruneSets: [],
      loading: false,
      running: {},
      headers: [
        { text: 'Prune Set', value: 'id', sortable: false, width: '15%' },
        { text: 'Client', value: 'client', sortable: false, width: '25%' },
        {
          text: 'Items from PDF',
          value: 'items_count',
          sortable: false,
          width: '8%',
        },
        { text: 'Entity', value: 'company_id', sortable: false, width: '22%' },
        { text: 'Actions', value: 'actions', sortable: false, width: '15%' },
        {
          text: 'Preview (populated on dry-run)',
          value: 'preview',
          sortable: false,
          width: '15%',
        },
        { text: '', value: 'delete', sortable: false, width: '5%' },
      ],
    };
  },
  computed: {
    ...mapState('companies', ['companies', 'loadingCompanies']),
    ...mapGetters({
      currentUser: 'user',
    }),
    companiesWithEmail() {
      return this.companies.map(c => ({
        ...c,
        nameWithEmail: `${c.tax_return_name} (${c.user.email})`,
      }));
    },
    canLoad() {
      return Boolean(this.extractedDocumentId) && Boolean(this.year);
    },
    isSorabanEmployee() {
      return Boolean(
        this.currentUser &&
          this.currentUser.email &&
          this.currentUser.email.toLowerCase().includes('@soraban.com'),
      );
    },
    importTotalCount() {
      return (
        this.successfullyImportedDocuments.length +
        this.documentsInProgress.length
      );
    },
    importProgressPercent() {
      const total = this.importTotalCount;
      if (!total) return 0;
      return Math.round(
        (this.successfullyImportedDocuments.length / total) * 100,
      );
    },
    isImportReady() {
      return (
        !this.priorYearDocStatusLoading &&
        this.importTotalCount > 0 &&
        this.documentsInProgress.length === 0
      );
    },
  },
  methods: {
    async loadPriorYearDocStatus() {
      try {
        this.priorYearDocStatusLoading = true;
        const response = await API.priorYearItemsMatch.getPriorYearDocumentsStatus(
          this.year,
        );
        const docs = (response && response.data) || [];
        const ultrataxDocs = docs.filter(
          d => d.document_type === 'ultratax_input_screens',
        );
        this.successfullyImportedDocuments = ultrataxDocs.filter(
          doc => doc.status === 'action_required',
        );
        this.documentsInProgress = ultrataxDocs.filter(
          doc => doc.status === 'processing',
        );
      } catch (e) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Failed to load import progress.',
        });
      } finally {
        this.priorYearDocStatusLoading = false;
      }
    },
    async loadDocuments() {
      try {
        this.documentsLoading = true;
        const response = await API.ultrataxInputScreensPrune.getDocuments({
          year: this.year,
        });
        const docs = response.data || [];
        this.documents = docs.map(d => ({
          ...d,
          label: `${d.name} (#${d.id}) • remaining ${d.remaining_count} • ${d.prune_set_count} total`,
        }));
      } catch (e) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Failed to load UltraTax documents.',
        });
      } finally {
        this.documentsLoading = false;
      }
    },
    async removeAllUploadedFilesForRolloverItems() {
      if (
        !confirm(
          'Are you sure you want to remove all uploaded files (both processed and in-processing) for rollover items? This is not reversible, and you need to re-upload the files again.',
        )
      )
        return;

      try {
        this.removingUploadedFiles = true;
        await API.priorYearItemsMatch.removeAllUploadedFilesForRolloverItems(
          this.year,
        );
        this.$store.dispatch('application/setSuccessNotification', {
          message: `Successfully removed all uploaded files for rollover items. Please refresh the page in a few minutes.`,
        });
        await this.loadPriorYearDocStatus();
        await this.loadDocuments();
      } catch (e) {
        this.$store.dispatch('application/setErrorNotification', {
          message: `There was a problem removing all uploaded files for rollover items.`,
        });
      } finally {
        this.removingUploadedFiles = false;
      }
    },
    async confirmDeleteAllPruneSets() {
      if (
        !confirm(
          'This will delete all prune sets for all documents in the list, and cannot be undone. Would you like to proceed?',
        )
      )
        return;

      await this.deleteAllPruneSets();
    },
    async deleteAllPruneSets() {
      try {
        this.deletingAllPruneSets = true;
        const response = await API.ultrataxInputScreensPrune.deleteAllPruneSets(
          {
            year: this.year,
          },
        );
        this.$store.dispatch('application/setSuccessNotification', {
          message:
            (response && response.data && response.data.message) ||
            'All prune sets deleted.',
        });
        await this.loadDocuments();
        this.pruneSets = [];
      } catch (e) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Failed to delete prune sets.',
        });
      } finally {
        this.deletingAllPruneSets = false;
      }
    },
    async openDocument(id) {
      this.extractedDocumentId = id;
      await this.loadPruneSets();
    },
    async loadPruneSets() {
      if (!this.canLoad) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Please enter Year and ExtractedDocument ID first.',
        });
        return;
      }
      try {
        this.loading = true;
        const response = await API.ultrataxInputScreensPrune.getPruneSets({
          extractedDocumentId: this.extractedDocumentId,
          year: this.year,
        });
        this.pruneSets = response.data || [];
      } catch (e) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Failed to load prune sets.',
        });
      } finally {
        this.loading = false;
      }
    },
    async saveCompany(pruneSet, companyId) {
      try {
        await API.ultrataxInputScreensPrune.updatePruneSet({
          id: pruneSet.id,
          companyId,
        });
        pruneSet.company_id = companyId;
        pruneSet.status = companyId ? 'ready' : 'unmatched';
      } catch (e) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Failed to update prune set company.',
        });
      }
    },
    async dryRun(pruneSet) {
      try {
        this.$set(this.running, pruneSet.id, 'dry_run');
        const response = await API.ultrataxInputScreensPrune.dryRun({
          extractedDocumentId: this.extractedDocumentId,
          year: this.year,
          priorYearPruneSetId: pruneSet.id,
        });
        const row =
          (response.data &&
            response.data.results &&
            response.data.results[0]) ||
          null;
        pruneSet.preview = row || pruneSet.preview;

        // If we determine a prune set is empty when we dry run, just delete it
        if (row && row.would_delete_count === 0) {
          await API.ultrataxInputScreensPrune.deletePruneSet({
            id: pruneSet.id,
          });
          await this.loadDocuments();
          this.pruneSets = this.pruneSets.filter(p => p.id !== pruneSet.id);
          this.$store.dispatch('application/setSuccessNotification', {
            message: 'Nothing to delete; prune set removed.',
          });
          return;
        }

        this.$store.dispatch('application/setSuccessNotification', {
          message: 'Dry-run complete.',
        });
      } catch (e) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Dry-run failed.',
        });
      } finally {
        this.$set(this.running, pruneSet.id, null);
      }
    },
    async executePrune(pruneSet) {
      if (
        !confirm(
          'Are you sure you want to execute prune? This will delete Organizer items for the selected entity/year.',
        )
      )
        return;

      try {
        this.$set(this.running, pruneSet.id, 'execute');
        const response = await API.ultrataxInputScreensPrune.execute({
          extractedDocumentId: this.extractedDocumentId,
          year: this.year,
          priorYearPruneSetId: pruneSet.id,
        });
        const row =
          (response.data &&
            response.data.results &&
            response.data.results[0]) ||
          null;
        pruneSet.preview = row || pruneSet.preview;
        pruneSet.status = 'pruned';
        this.pruneSets = this.pruneSets.filter(p => p.id !== pruneSet.id);
        await this.loadDocuments();
        this.$store.dispatch('application/setSuccessNotification', {
          message: 'Prune executed.',
        });
      } catch (e) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Prune failed.',
        });
      } finally {
        this.$set(this.running, pruneSet.id, null);
      }
    },
    async deletePruneSet(pruneSet) {
      try {
        await API.ultrataxInputScreensPrune.deletePruneSet({ id: pruneSet.id });
        this.pruneSets = this.pruneSets.filter(p => p.id !== pruneSet.id);
        await this.loadDocuments();
        this.$store.dispatch('application/setSuccessNotification', {
          message: 'Prune set removed.',
        });
      } catch (e) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Failed to delete prune set.',
        });
      }
    },
  },
  async created() {
    if (!this.isSorabanEmployee) return;
    await this.$store.dispatch('companies/getCompanies');

    // Allow direct linking/bookmarking into a specific run.
    // Example: /ultratax-prune-sets?extracted_document_id=2&year=2025
    const q = this.$route && this.$route.query ? this.$route.query : {};
    const qDocId = q.extracted_document_id || q.extractedDocumentId;
    const qYear = q.year;
    if (qDocId) this.extractedDocumentId = qDocId;
    if (qYear) this.year = Number(qYear);

    await this.loadPriorYearDocStatus();
    await this.loadDocuments();

    if (qDocId && qYear) {
      await this.loadPruneSets();
    }
  },
  watch: {
    year() {
      this.loadPriorYearDocStatus();
      this.loadDocuments();
      this.extractedDocumentId = '';
      this.pruneSets = [];
    },
  },
};
</script>

<style scoped>
.documents-table-wrapper {
  margin-bottom: 16px;
}
.documents-data-table {
  min-height: 0;
}
/* Vuetify 1.5 renders internal wrapper nodes that won't match scoped CSS unless we use a deep selector. */
.documents-data-table >>> .v-datatable__wrapper,
.documents-data-table >>> .v-table__overflow {
  max-height: 450px;
  overflow-y: auto;
}
.documents-data-table >>> thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: white;
}
.v-data-table .v-select,
.v-data-table .v-btn {
  font-size: 0.85rem;
}
.v-data-table tbody td,
.v-data-table tbody th {
  padding-top: 6px;
  padding-bottom: 6px;
}
</style>
